import { useUnsavedGuard } from "@/ui/useUnsavedGuard";
import { LEVEL_LABEL } from "@/ui/vocabulary";
import { RecordAfterFact } from "@/ui/RecordAfterFact";
import { FileField, FileList, DOCUMENT_TYPES } from "@/ui/FileField";
import { SavedPrint, type SavedRendition } from "@/ui/SavedPrint";
import { useEffect, useMemo, useState } from "react";
import {
  intakeAcknowledge,
  intakePrint,
  intakeMarkReady,
  intakeOpen,
  intakeRecordHeld,
  intakeSavePart0,
  intakeSavePart1,
  intakeSaveCompNotes,
  intakeCommentPart1,
  intakeSendPart1,
  delegationGrant,
  delegationList,
  type IntakeOpenOutput,
} from "@/api/tools";
import { isClientRefusal, useRead, useWrite } from "@/api/useTool";
import { McPage } from "@/shell/page";
import { ScheduleSection, ScheduleStandalone } from "@/views/scheduling/ScheduleSection";
import { StandInDialog } from "@/views/Access";
import { go } from "@/router";
import { useSession } from "@/session/useSession";
import { useViewState } from "@/shell/viewState";
import { Banner, Empty, Field, KV, ObjectReadGuard, Pill, Receipt, fmtDate, fmtMoney, recordLabel } from "@/ui";
import { CardGrid, GridCell, McRoot, RecordCard, Skeleton } from "@/mc";
import { PlanEditor } from "@/views/plan/PlanEditor";
import { RecordHeld, type HeldAttendee } from "@/views/plan/RecordHeld";
import { LogisticsFields } from "@/views/intake/LogisticsFields";
import { logisticsPayload } from "@/views/intake/logistics";
import { LOGISTICS_LABELS, heldOfflinePart2Open, heldOfflineReason } from "@/views/intake/labels";
import { REQ_V1_ACTIONS } from "@/views/requisition/labels";

// V1-INTERACTIONS §4.5 — Intake & Panel: the hand-off strip, Part 0 (Recruiter), Part 1 (Hiring Manager) and the call.
// Part 2 is the interview plan (lane B4, PLAN §1 D18): `IntakePage` sends `part2` — and the strip's own default once
// the call is held — to the interview-plan editor (`views/plan/PlanEditor.tsx`, its own data path on intake.plan_open,
// inside the design system's root), and the call's record-held dialog is `views/plan/RecordHeld.tsx` ("HM + Recruiter
// required; HRBP optional."). The V1 body below is `IntakePageV1`. `#requisitions/R-####/intake` and its parts
// `…/intake/part0|part1|call` are lane B4's Intake & Panel tab (views/b4/register.ts, UAT D-10, F-186), which draws
// `IntakeTabBody` under the requisition's own header and tabs — the header's Actions menu carries Print the intake form
// and Record what already happened, so the tab draws neither; `…/intake/part2`, and the call for a Coordinator who may
// not read the intake, keep this page.

type Intake = IntakeOpenOutput;
const ROOTS = ["respect", "ownership", "opportunity", "together", "service"] as const;
const ROOTS_LABEL: Record<string, string> = { respect: "Respect", ownership: "Ownership", opportunity: "Opportunity", together: "Together", service: "Service" };
const PART1_LABEL: Record<string, string> = { preferences_logistics: LOGISTICS_LABELS.section, skills_experience: "Candidate requirements — skills & experience", assessment_profile: "Candidate requirements — assessment & profile", role_narrative: "Role narrative & team culture" };
// A field name is never shown to a reader: every key the server names (part0_missing, part0_locked_fields) is printed
// with the label the form itself carries above that control.
const PART0_LABEL: Record<string, string> = {
  kickoff_date: "Kickoff date",
  team_size_structure: "Team size / structure",
  job_description: "Job description",
  bonus_structure: "Bonus / incentive structure",
  sourcing_notes: "Sourcing & logistics notes",
  comp_approval_notes: "Comp approval notes",
  req_no: "REQ #",
  title: "Job title",
  level: "Role level",
  comp_min: "Comp range (minimum)",
  comp_max: "Comp range (maximum)",
};
const fieldLabels = (keys: string[]) => keys.map((k) => PART0_LABEL[k] ?? k.replace(/_/g, " ")).join(", ");

export function IntakePage({ id, part }: { id: string; part: string }) {
  if (part === "part2") return <PlanMount id={id} />;
  return <IntakePageV1 id={id} part={part} />;
}

/** The requisition's Intake & Panel tab (UAT D-10): the page's body without V1's header — no heading, lede, link back
 * to the requisition or print button, which the requisition's header, tabs and Actions menu carry (F-186) — and without
 * the record-after-the-fact control, which is in that menu; it opens on the hand-off the intake is at. */
export function IntakeTabBody({ id, part = "" }: { id: string; part?: string | undefined }) {
  return <IntakePageV1 id={id} part={HOSTED_PARTS.has(part) ? part : ""} hosted />;
}

/** F-185, F-186: inside the tab the hand-offs choose the part in place (`…/intake/part1`, the tab's own route), so the
 * requisition's header and tabs stay; Part 2 is the requisition's Plan tab. */
export const HOSTED_PARTS: ReadonlySet<string> = new Set(["part0", "part1", "call"]);
const hostedHref = (rid: string) => (key: string) => (key === "part2" ? `#requisitions/${rid}/plan` : `#requisitions/${rid}/intake/${key}`);

/** F-186: Print the intake form as the requisition header's Actions menu runs it — the receipted export (intake.print),
 * then its saved rendition in the print dialog, which styles/print.css prints (never the live screen). */
export function useIntakePrint(id: string) {
  const [printed, setPrinted] = useState<SavedRendition | null>(null);
  const print = useWrite((input: Parameters<typeof intakePrint>[0]) => intakePrint(input));
  const run = () => {
    if (!print.isPending) void print.mutateAsync({ requisition_id: id }).then(setPrinted).catch(() => undefined);
  };
  const dialog = <SavedPrint title="Printable intake form" value={printed} kind="intake" onClose={() => setPrinted(null)} />;
  return { run, busy: print.isPending, error: print.error, dialog };
}

/** The interview plan: the editor in the design system's root; a refused read keeps V1's not-found page outside it. */
function PlanMount({ id }: { id: string }) {
  return <PlanEditor id={id} refused={(query) => <ObjectReadGuard query={query} />} />;
}

function IntakePageV1({ id, part, hosted = false }: { id: string; part: string; hosted?: boolean }) {
  const { register } = useViewState();
  const { can } = useSession();
  const [printed, setPrinted] = useState<SavedRendition | null>(null);
  const print = useWrite((input: Parameters<typeof intakePrint>[0]) => intakePrint(input));
  const q = useRead(["intake", id], () => intakeOpen({ requisition_id: id }));
  useEffect(() => register({ view: "intake", ids: { requisition: id }, summary: `intake ${id} ${part || "strip"} ${q.data?.status ?? ""}` }), [register, id, part, q.data?.status]);
  // the Coordinator schedules the intake call without reading the intake form: the call's scheduling card is their page
  if (q.error && isClientRefusal(q.error) && part === "call") return <ScheduleStandalone subject={{ kind: "intake", rid: id }} route={`requisitions/${id}/intake/call`} />;
  // inside the requisition's tab the read in flight is the tab's own skeleton, in place under the tabs
  if (hosted && !q.data && !q.error) return <Skeleton lines={6} />;
  if (!q.data || q.error) return <ObjectReadGuard query={q} />;
  const it = q.data;
  // F-185: inside the tab Part 2 is the requisition's Plan tab, so a held or finalized intake opens on the call
  const active = part || (hosted && defaultPart(it) === "part2" ? "call" : defaultPart(it));
  // F-185 (the page grammar): inside the requisition's tab the hand-offs are one stepper card and the part is one form card
  if (hosted && active === "part2") {
    return (
      <CardGrid>
        <RecordCard variant="form" span="all"><Strip it={it} active={active} hrefOf={hostedHref(id)} /></RecordCard>
        <GridCell span="all"><PlanMount id={id} /></GridCell>
      </CardGrid>
    );
  }
  if (active === "part2") return <PlanMount id={id} />;
  if (hosted) {
    return (
      <CardGrid>
        <RecordCard variant="form" span="all"><Strip it={it} active={active} hrefOf={hostedHref(id)} /></RecordCard>
        <RecordCard variant="form" span="all">
          <div className="screen-only">
            {it.status === "finalized" && (
              <p className="muted" role="note">
                The interview plan is finalized, so Part 0 and Part 1 are read-only. Part 0 can be edited again once the <a href={`#requisitions/${it.requisition_id}/plan`}>interview plan</a> is reopened with a reason. Part 1 stays read-only because it closed when the call was held.
              </p>
            )}
            {active === "part0" && <Part0 it={it} />}
            {active === "part1" && <Part1 it={it} />}
            {active === "call" && <Call it={it} />}
          </div>
        </RecordCard>
      </CardGrid>
    );
  }
  const printForm = can("intake.print") && <button className="btn" disabled={print.isPending} onClick={() => void print.mutateAsync({ requisition_id: id }).then(setPrinted).catch(() => undefined)}>{REQ_V1_ACTIONS.printIntake}</button>;
  return (
    <div className="screen-only"><RecordAfterFact id={id} family="requisition" section="intake" /><p className="paper-only">Use Print the intake form to print its saved version with an export receipt.</p>
      <div className="hrow">
        <div>
          <h1 className="page">Intake & Panel · {it.requisition_id}</h1>
          <p className="lede">
            {it.part0.title as string} · {it.status_label} · panel v{it.panel_version} · guide v{it.guide_version}
          </p>
        </div>
        <div className="acts">
          {printForm}
          <a className="btn btn--sm" href={`#requisitions/${id}`}>
            ← Requisition
          </a>
        </div>
      </div>
      <Banner error={print.error} />
      <SavedPrint title="Printable intake form" value={printed} kind="intake" onClose={() => setPrinted(null)} />
      <Strip it={it} active={active} />
      {/* F-174: every earlier part stays readable once the plan is finalized; Part 0 edits again after Reopen (save_part0), Part 1 closed at the held call (save_part1) */}
      {it.status === "finalized" && (
        <p className="muted" role="note">
          The interview plan is finalized, so Part 0 and Part 1 are read-only. Part 0 can be edited again once the <a href={`#requisitions/${it.requisition_id}/intake/part2`}>interview plan</a> is reopened with a reason. Part 1 stays read-only because it closed when the call was held.
        </p>
      )}
      {active === "part0" && <Part0 it={it} />}
      {active === "part1" && <Part1 it={it} />}
      {active === "call" && <Call it={it} />}
    </div>
  );
}

/** The requisition's range as the read carries it, or null when the read carries none (the Coordinator's). */
function p0Comp(it: Intake): string | null {
  const { comp_min: min, comp_max: max } = it.part0 as Record<string, unknown>;
  return min == null && max == null ? null : `${String(min ?? "")}–${String(max ?? "")}`;
}

/** The reason the intake call was recorded held offline (DC-237), or null when it was not. */
function heldOffline(it: Intake): string | null {
  const m = it.meeting;
  return m && m.held_at && m.held_offline === true ? String(m.offline_reason ?? "") : null;
}

/** True for a reader with no seat on the requisition: a Coordinator in its pool or a TA Manager (B13 F-160, F-151;
 * C7). They read the strip, the call card, Part 1's two lines and the plan, never the Part 1 and Part 2 editors. */
function readsWithoutSeat(it: Intake, me: { person_id: string; roles: string[] } | null | undefined): boolean {
  if (!me || me.roles.includes("admin")) return false;
  const seats = it.part0 as Record<string, unknown>;
  return ![seats.hiring_manager_id, seats.recruiter_id, seats.hrbp_id].includes(me.person_id);
}

/** Part 1's two lines (C4 `part1_text`), as print page 2 reads them: the HM's windows and the logistics choices. */
function Part1Lines({ it }: { it: Intake }) {
  const text = it.part1_text;
  return (
    <section className="card" style={{ marginTop: 12 }}>
      <h3 className="sub">{LOGISTICS_LABELS.section}</h3>
      <KV
        items={[
          [LOGISTICS_LABELS.hmAvailability, text?.availability_line ?? "—"],
          [LOGISTICS_LABELS.section, text?.logistics_line ?? "—"],
        ]}
      />
    </section>
  );
}

function defaultPart(it: Intake): string {
  if (it.status === "part0_open") return "part0";
  if (it.status === "part1_sent") return it.can.save_part1 ? "part1" : "call";
  if (it.status === "scheduled") return "call";
  return "part2";
}

// UAT F-45: one current card. Each part is handed on by the fact the server records — Part 0 when Part 1 is sent, Part 1
// when it is ready, the call when it is held, Part 2 when the intake is finalized — and a later hand-off closes every
// earlier one, so the current card is the first part not yet handed on and a finalized intake has every card done. A
// later part is locked while the step it waits for has not happened, and otherwise shows only its owner. Every card
// carries its number in hand-off order, the call included; the state is in the card's words, never a glyph.
function Strip({ it, active, hrefOf }: { it: Intake; active: string; hrefOf?: ((key: string) => string) | undefined }) {
  const cards: { key: string; label: string; owner: string; handedOn: boolean; waits: string | null }[] = [
    { key: "part0", label: "Part 0 · Pre-fill", owner: "Recruiter", handedOn: !!it.part1_sent_at, waits: null },
    { key: "part1", label: "Part 1 · Before the call", owner: "Hiring Manager", handedOn: !!it.part1_ready_at, waits: it.part1_sent_at ? null : "after Part 0 is sent" },
    { key: "call", label: "The intake call", owner: "Recruiter", handedOn: !!it.meeting?.held_at, waits: it.meeting || it.part1_sent_at ? null : "after Part 1 is sent" },
    { key: "part2", label: "Part 2 · Interview plan", owner: "Hiring Manager · Recruiter", handedOn: it.status === "finalized", waits: it.meeting?.held_at ? null : "after the call is held" },
  ];
  const reached = cards.reduce((n, c, at) => (c.handedOn ? at + 1 : n), 0);
  const offlineReason = heldOffline(it);
  return (
    <ol className="strip" aria-label="Hand-offs">
      {cards.map((c, at) => {
        const state = at < reached ? "done" : at === reached ? "current" : c.waits ? "locked" : "next";
        return (
          <li key={c.key} className={`strip__card strip__card--${state}${active === c.key ? " strip__card--active" : ""}`}>
            <a href={hrefOf ? hrefOf(c.key) : `#requisitions/${it.requisition_id}/intake/${c.key}`} aria-current={active === c.key ? "page" : undefined}>
              <span className="sequence-tile" aria-hidden="true">{String(at + 1).padStart(2, "0")}</span>
              <b className="sequence-kicker">{c.label}</b>
              <span className="muted">
                {state === "next" ? c.owner : `${c.owner} · ${state === "locked" ? c.waits : state}`}
              </span>
              {c.key === "call" && offlineReason !== null && (
                <span className="muted strip__note" title={offlineReason}>
                  {heldOfflineReason({ reason: offlineReason })}
                </span>
              )}
            </a>
          </li>
        );
      })}
    </ol>
  );
}

// ---- Part 0 --------------------------------------------------------------------------------------------------------------

function Part0({ it }: { it: Intake }) {
  const rid = it.requisition_id;
  const { me } = useSession();
  // a Coordinator reads no compensation figure on the tab (DC-239): the row is absent, never "— – —"
  const compRow: [string, string][] = readsWithoutSeat(it, me) && p0Comp(it) === null ? [] : [["Comp range", `${fmtMoney(it.part0.comp_min as string | null)} – ${fmtMoney(it.part0.comp_max as string | null)}`]];
  const [compNotes, setCompNotes] = useState(String(it.part0.comp_approval_notes ?? ""));
  const compSave = useWrite((i: Parameters<typeof intakeSaveCompNotes>[0], k) => intakeSaveCompNotes(i, k), [["intake", rid], ["requisition", rid]]);
  const [descriptionFile, setDescriptionFile] = useState<string | null>(it.part0.job_description_file_id ? String(it.part0.job_description_file_id) : null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [f, setF] = useState({ kickoff_date: String(it.part0.kickoff_date ?? ""), team_size_structure: String(it.part0.team_size_structure ?? ""), sourcing_notes: String(it.part0.sourcing_notes ?? ""), job_description: String(it.part0.job_description ?? ""), bonus_structure: String(it.part0.bonus_structure ?? ""), reason: "" });
  const save = useWrite((i: Parameters<typeof intakeSavePart0>[0], k) => intakeSavePart0(i, k), [["intake", rid], ["requisition", rid], ["today"]]);
  const send = useWrite((i: { requisition_id: string }, k) => intakeSendPart1(i, k), [["intake", rid], ["today"]]);
  const posted = !!it.part0.req_no && it.part0_locked_fields.includes("req_no");
  const p0 = it.part0;
  const [saved, setSaved] = useState({ f, descriptionFile, compNotes });
  const guard = useUnsavedGuard(JSON.stringify({ f, descriptionFile, compNotes }) !== JSON.stringify(saved), "Intake Part 0", () => { setF(saved.f); setDescriptionFile(saved.descriptionFile); setCompNotes(saved.compNotes); });
  return (
    <div className="grid grid--2">
      {guard.dialog}
      <section className="card">
        <h2 className="sec">Job details & role level</h2>
        <KV
          items={[
            ["REQ #", p0.req_no ? String(p0.req_no) : "not posted yet"],
            ["Requisition approved by / date", it.approved_by ? `${it.approved_by} · ${fmtDate(it.approved_on)}` : "—"],
            ["Job title", String(p0.title)],
            ["Role level", LEVEL_LABEL[String(p0.level)] ?? String(p0.level)],
            ["Department", String(p0.department)],
            ["Reports to", String(p0.reports_to_title)],
            ["Location(s)", (p0.locations as string[]).join("; ")],
            ["New / Replacement", p0.reason === "backfill" ? "Replacement" : "New"],
            ["# of openings", String(p0.openings)],
            ["Posting scope", recordLabel(String(p0.posting_scope))],
            ...compRow,
            ["Hiring Manager · Recruiter · HRBP", `${p0.hiring_manager} · ${p0.recruiter} · ${p0.hrbp}`],
          ]}
        />
        {it.part0_locked_fields.length > 0 && <p className="muted">Locked: {fieldLabels(it.part0_locked_fields)} (changes go through the requisition with a reason).</p>}
        {it.can.comp_notes ? <form onSubmit={e => { e.preventDefault(); void compSave.mutateAsync({ requisition_id: rid, comp_approval_notes: compNotes }).then(() => setSaved(current => ({ ...current, compNotes }))).catch(() => undefined); }}>
          <Field label="Comp approval notes" id="p0-comp-notes" hint="Your notes are retained with the requisition." error={compSave.error?.fieldErrors.comp_approval_notes}><textarea id="p0-comp-notes" rows={4} maxLength={2000} value={compNotes} onChange={e => setCompNotes(e.target.value)} aria-describedby="p0-comp-notes-hint" /></Field>
          <Banner error={compSave.error} /><button className="btn" disabled={compSave.isPending}>{compSave.isPending ? "Saving…" : "Save comp notes"}</button>
          {compSave.isSuccess && <Receipt>Comp approval notes saved.</Receipt>}
        </form> : p0.comp_approval_notes ? <p className="muted">Comp approval notes: {String(p0.comp_approval_notes)}</p> : null}
      </section>
      <section className="card">
        <h2 className="sec">Recruiter pre-fill</h2>
        {p0.job_description_file_id ? <FileList label="Job description" files={[{ file_id: String(p0.job_description_file_id) }]} /> : null}
        {it.can.save_part0 ? (
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void save.mutateAsync({ requisition_id: rid, kickoff_date: f.kickoff_date || null, team_size_structure: f.team_size_structure || null, sourcing_notes: f.sourcing_notes || null, job_description: f.job_description || null, ...(descriptionFile ? { job_description_file_id: descriptionFile } : {}), bonus_structure: f.bonus_structure || null, reason: f.reason || null }).then(() => setSaved(current => ({ ...current, f, descriptionFile }))).catch(() => undefined);
            }}
          >
            <Field label="Kickoff date" required id="p0-kick" error={save.error?.fieldErrors.kickoff_date}>
              <input id="p0-kick" type="date" value={f.kickoff_date} onChange={(e) => setF({ ...f, kickoff_date: e.target.value })} />
            </Field>
            <Field label="Team size / structure" required id="p0-team">
              <textarea id="p0-team" rows={2} value={f.team_size_structure} onChange={(e) => setF({ ...f, team_size_structure: e.target.value })} />
            </Field>
            <FileField label="Attach description document" owner={{ owner_type: "requisition", owner_id: rid, requisition_id: rid, kind: "intake_pdf" }} accept={DOCUMENT_TYPES} disabled={save.isPending || uploadBusy} onBusy={setUploadBusy} onChange={(file) => setDescriptionFile(file?.file_id ?? (p0.job_description_file_id ? String(p0.job_description_file_id) : null))} />
            <Field label="Job description" required id="p0-jd" hint="Required before posting. The saved document stays attached when you edit this text; upload a replacement to change the document." error={save.error?.fieldErrors.job_description}>
              <textarea id="p0-jd" rows={6} value={f.job_description} onChange={(e) => setF({ ...f, job_description: e.target.value })} />
            </Field>
            <Field label="Bonus / incentive structure" id="p0-bonus">
              <input id="p0-bonus" value={f.bonus_structure} onChange={(e) => setF({ ...f, bonus_structure: e.target.value })} />
            </Field>
            <Field label="Sourcing & logistics notes" id="p0-notes">
              <textarea id="p0-notes" rows={3} value={f.sourcing_notes} onChange={(e) => setF({ ...f, sourcing_notes: e.target.value })} />
            </Field>
            {posted && (
              <Field label="Reason for the change (the position is posted)" required id="p0-reason" error={save.error?.fieldErrors.reason}>
                <input id="p0-reason" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />
              </Field>
            )}
            <Banner error={save.error} />
            <div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <button type="submit" className="btn" disabled={save.isPending || uploadBusy}>
                {save.isPending ? "Saving…" : "Save"}
              </button>
              {it.status === "part0_open" && (
                <button type="button" className="btn btn--primary" disabled={!it.can.send_part1 || send.isPending} onClick={() => void send.mutateAsync({ requisition_id: rid }).then(() => go(`requisitions/${rid}/intake/call`))}>
                  {send.isPending ? "Sending…" : "Send Part 1 to the Hiring Manager"}
                </button>
              )}
              {it.status === "part0_open" && !it.can.send_part1 && <span className="muted">Missing: {fieldLabels(it.part0_missing)}</span>}
              {it.part1_sent_at && <span className="muted">Part 1 sent {fmtDate(it.part1_sent_at, true)} · due {fmtDate(it.part1_due_at, true)}</span>}
            </div>
            <Banner error={send.error} />
            {save.isSuccess && <Receipt>Saved.</Receipt>}
          </form>
        ) : (
          <KV items={[["Kickoff date", String(p0.kickoff_date ?? "—")], ["Team size / structure", String(p0.team_size_structure ?? "—")], ["Sourcing notes", String(p0.sourcing_notes ?? "—")]]} />
        )}
      </section>
    </div>
  );
}

// ---- Part 1 --------------------------------------------------------------------------------------------------------------

// The required marks are the server's Part 1 rule (PART1_REQUIRED in tools/intake.py), and an assessment that is
// required also needs its description and round before the section is ready (UAT F-45).
const assessmentRequired = (values: Record<string, unknown>) => values.assessment_required === true;
const SECTION_FIELDS: Record<string, { key: string; label: string; kind: "text" | "textarea" | "select" | "list" | "number" | "bool"; required?: boolean | ((values: Record<string, unknown>) => boolean); options?: string[] }[]> = {
  // B13 (DC-231: F-156, F-157; contract C4): step 2 is drawn by views/intake/LogisticsFields.tsx — HM availability as
  // windows, Work-from-home policy, Relocation package and Travel as choices, and the Logistics note
  preferences_logistics: [],
  skills_experience: [
    { key: "hard_skills", label: "Hard skills (one per line; add ' (preferred)' where not required)", kind: "list", required: true },
    { key: "soft_skills", label: "Soft skills (one per line)", kind: "list", required: true },
    { key: "coachable_skills", label: "Coachable skills", kind: "list" },
    { key: "tools_platforms", label: "Tools / platforms", kind: "list" },
    { key: "degree_requirement", label: "Degree requirement", kind: "select", required: true, options: ["required", "preferred", "experience_can_substitute", "none"] },
    { key: "minimum_years", label: "Minimum years", kind: "number", required: true },
    { key: "qsr_foodservice", label: "QSR / foodservice experience", kind: "select", options: ["required", "preferred", "not_needed"] },
    { key: "franchise_system", label: "Franchise system experience", kind: "select", options: ["required", "preferred", "not_needed"] },
    { key: "target_companies", label: "Target companies", kind: "list" },
    { key: "other_titles", label: "Other titles to consider", kind: "list" },
  ],
  assessment_profile: [
    { key: "assessment_required", label: "Skills assessment required?", kind: "bool", required: true },
    { key: "assessment_description", label: "Assessment description", kind: "textarea", required: assessmentRequired },
    { key: "assessment_round", label: "Which round", kind: "text", required: assessmentRequired },
    { key: "evaluation_criteria", label: "Evaluation criteria", kind: "list" },
    { key: "ideal_candidate_profile", label: "Ideal candidate profile", kind: "textarea" },
    { key: "other_requirements", label: "Other notes", kind: "textarea" },
  ],
  role_narrative: [
    { key: "first_30_days", label: "First 30 days", kind: "textarea" },
    { key: "day_in_the_life", label: "A day in the life", kind: "textarea" },
    { key: "team_culture", label: "Team culture", kind: "textarea" },
  ],
};

// One draft per section, held above the four forms and kept in sessionStorage, so that moving to another hand-off card
// and back, a re-render or a re-authentication never throws away typing, and so saving ONE section replaces only that
// section's server state and leaves the other three drafts untouched. An explicit sign-out clears sessionStorage
// (session/useSession.tsx: "drafts kept for re-authentication are discarded on an explicit sign-out").
interface SectionDraft {
  values: Record<string, unknown>;
  raw: Record<string, string>;
}
type Part1Drafts = Record<string, SectionDraft>;
// keyed by the person as well as the requisition: what one Hiring Manager typed is never read back by anyone else; v2
// since B13 (C4): step 2 is stored structured, so a draft typed into the old free-text boxes is never read back
const draftsKey = (who: string, rid: string) => `ta.intake.${who}.${rid}.part1.v2`;

function readDrafts(who: string, rid: string): Part1Drafts {
  try {
    const stored = sessionStorage.getItem(draftsKey(who, rid));
    return stored ? (JSON.parse(stored) as Part1Drafts) : {};
  } catch {
    return {}; // storage unavailable or unreadable: the drafts live for this visit only
  }
}

function writeDrafts(who: string, rid: string, drafts: Part1Drafts): void {
  try {
    sessionStorage.setItem(draftsKey(who, rid), JSON.stringify(drafts));
  } catch {
    /* storage unavailable: the drafts live for this visit only */
  }
}

/** the section as the server holds it right now — the seed for an untouched section, and what a saved section takes back */
function serverSection(it: Intake, section: string): SectionDraft {
  return { values: { ...((it.part1[section] as Record<string, unknown> | undefined) ?? {}) }, raw: {} };
}

function seedDrafts(who: string, rid: string, it: Intake): Part1Drafts {
  const kept = readDrafts(who, rid);
  const seeded: Part1Drafts = {};
  for (const section of it.part1_sections) seeded[section] = kept[section] ?? serverSection(it, section);
  return seeded;
}

function Part1({ it }: { it: Intake }) {
  const rid = it.requisition_id;
  const { me } = useSession();
  const save = useWrite((i: { requisition_id: string; section: "preferences_logistics" | "skills_experience" | "assessment_profile" | "role_narrative" | "job_description"; values: Record<string, unknown> }, k) => intakeSavePart1(i, k), [["intake", rid]]);
  const ack = useWrite((i: { requisition_id: string }, k) => intakeAcknowledge(i, k), [["intake", rid]]);
  const ready = useWrite((i: { requisition_id: string }, k) => intakeMarkReady(i, k), [["intake", rid], ["today"]]);
  const who = me?.person_id ?? "anon";
  const [drafts, setDrafts] = useState<Part1Drafts>(() => seedDrafts(who, rid, it));
  const [justSaved, setJustSaved] = useState<string | null>(null);
  useEffect(() => {
    writeDrafts(who, rid, drafts);
  }, [who, rid, drafts]);
  // the save resolves once the intake has been read back, so the saved section takes the server's state; the others keep theirs
  useEffect(() => {
    if (!justSaved) return;
    setDrafts((d) => ({ ...d, [justSaved]: serverSection(it, justSaved) }));
    setJustSaved(null);
  }, [justSaved, it]);
  const saveSection = (section: string, values: Record<string, unknown>) =>
    save
      .mutateAsync({ requisition_id: rid, section: section as "preferences_logistics", values })
      .then(() => setJustSaved(section))
      .catch(() => undefined); // a refusal shows in the banner above the sections; the draft stays exactly as it was typed
  const serverDrafts = Object.fromEntries(it.part1_sections.map(section => [section, serverSection(it, section)]));
  const dirty = it.part1_sections.some(section => JSON.stringify(drafts[section] ?? serverDrafts[section]) !== JSON.stringify(serverDrafts[section]));
  const guard = useUnsavedGuard(dirty, "Intake Part 1", () => { setDrafts(serverDrafts); writeDrafts(who, rid, serverDrafts); });
  const done = it.part1_sections_complete.length;
  const canEdit = !!it.can.save_part1;
  const lineOnly = readsWithoutSeat(it, me);
  return (
    <div>
      {guard.dialog}
      <div className="row row--between" style={{ alignItems: "center" }}>
        <h2 className="sec">
          Part 1 — before the call <span className="sec__n">{done} of 4 sections ready</span>
        </h2>
        {it.part1_due_at && <span className="muted">Due {fmtDate(it.part1_due_at, true)}</span>}
      </div>
      <div className="progress" role="progressbar" aria-label="Intake sections ready" aria-valuemin={0} aria-valuemax={4} aria-valuenow={done} aria-valuetext={`${done} of 4 sections ready`}>
        <div className="progress__bar" style={{ width: `${(done / 4) * 100}%` }} />
      </div>
      {!it.part1_sent_at && <Empty heading="Waiting for Part 1">Part 1 opens for the Hiring Manager once the Recruiter sends it.</Empty>}
      {it.part1_sent_at && !canEdit && <Part1Lines it={it} />}
      {it.part1_sent_at && !lineOnly && <Banner error={save.error} />}
      {it.part1_sent_at && !lineOnly &&
        it.part1_sections.map((section) => (
          <div key={section}><SectionForm
            key={section}
            section={section}
            draft={drafts[section] ?? serverSection(it, section)}
            onChange={(next) => setDrafts((d) => ({ ...d, [section]: next }))}
            complete={it.part1_sections_complete.includes(section)}
            canEdit={canEdit}
            onSave={(values) => saveSection(section, values)}
            busy={save.isPending}
          /><Part1Comments it={it} section={section} /></div>
        ))}
      {it.part1_sent_at && !lineOnly && (
        <section className="card" style={{ marginTop: 12 }}>
          <h3 className="sub">Hiring Manager acknowledgment</h3>
          <label className="check">
            <input type="checkbox" checked={!!it.hm_acknowledged_at} disabled={!canEdit || !!it.hm_acknowledged_at || ack.isPending} onChange={() => void ack.mutateAsync({ requisition_id: rid })} />I have reviewed the recruiting intake form and confirm the requirements above reflect the role as I intend to hire it.
          </label>
          {it.hm_acknowledged_at && (
            <p className="muted">
              Recorded {fmtDate(it.hm_acknowledged_at, true)} · {me?.name}
            </p>
          )}
          <Banner error={ready.error ?? ack.error} />
          <div className="row" style={{ gap: 8, alignItems: "center", marginTop: 8 }}>
            <button className="btn btn--primary" disabled={!it.can.mark_ready || ready.isPending} onClick={() => void ready.mutateAsync({ requisition_id: rid })}>
              {ready.isPending ? "Marking…" : "Ready for the intake call"}
            </button>
            {it.part1_ready_at ? <Pill tone="ok">ready {fmtDate(it.part1_ready_at, true)}</Pill> : !it.can.mark_ready && canEdit ? <span className="muted">Complete every required section and the acknowledgment first.</span> : null}
          </div>
        </section>
      )}
    </div>
  );
}

function Part1Comments({ it, section }: { it: Intake; section: string }) {
  const [comment, setComment] = useState("");
  const save = useWrite((i: Parameters<typeof intakeCommentPart1>[0], k) => intakeCommentPart1(i, k), [["intake", it.requisition_id], ["today"]]);
  const comments = (Array.isArray(it.part1._comments) ? it.part1._comments : []).filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === "object" && (entry as Record<string, unknown>).section === section);
  if (!it.can.comment_part1 && comments.length === 0) return null;
  return <section className="card"><h3 className="sub">Comments · {PART1_LABEL[section] ?? section}</h3>
    {comments.map((entry, index) => <blockquote className="long" key={index}><p>{String(entry.comment ?? "")}</p><p className="muted">{fmtDate(String(entry.at), true)}</p></blockquote>)}
    {it.can.comment_part1 && <form onSubmit={e => { e.preventDefault(); void save.mutateAsync({ requisition_id: it.requisition_id, section, comment: comment.trim() }).then(() => setComment("")).catch(() => undefined); }}>
      <Field label={`Comment on ${PART1_LABEL[section] ?? section}`} id={`comment-${section}`}><textarea id={`comment-${section}`} rows={3} maxLength={2000} value={comment} onChange={e => setComment(e.target.value)} /></Field><Banner error={save.error} /><button className="btn" disabled={save.isPending || !comment.trim()}>Add comment</button>{save.isSuccess && <Receipt>Comment added — the Hiring Manager is notified.</Receipt>}
    </form>}
  </section>;
}

function SectionForm({ section, draft, onChange, complete, canEdit, onSave, busy }: { section: string; draft: SectionDraft; onChange: (next: SectionDraft) => void; complete: boolean; canEdit: boolean; onSave: (values: Record<string, unknown>) => void; busy: boolean }) {
  const fields = SECTION_FIELDS[section] ?? [];
  const v = draft.values;
  const raw = draft.raw;
  const setV = (values: Record<string, unknown>) => onChange({ values, raw });
  const setRaw = (next: Record<string, string>) => onChange({ values: v, raw: next });
  const [open, setOpen] = useState(!complete);
  const listToText = (x: unknown) => (Array.isArray(x) ? x.map((i) => (typeof i === "string" ? i : `${(i as { text: string }).text}${(i as { required_or_preferred?: string }).required_or_preferred === "preferred" ? " (preferred)" : ""}`)).join("\n") : "");
  const textToList = (s: string, structured: boolean) =>
    s
      .split("\n")
      .map((x) => x.trim())
      .filter(Boolean)
      .map((x) => (structured ? { text: x.replace(/\s*\(preferred\)\s*$/i, ""), required_or_preferred: /\(preferred\)\s*$/i.test(x) ? "preferred" : "required" } : x));
  return (
    <details className="card section" open={open} onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}>
      <summary>
        <span className={"section__state" + (complete ? " section__state--done" : "")} aria-hidden="true">
          {complete ? "✓" : "○"}
        </span>
        {PART1_LABEL[section] ?? section}
        {complete ? <Pill tone="ok">ready</Pill> : <Pill tone="muted">open</Pill>}
      </summary>
      <form
        className="form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          const merged: Record<string, unknown> = { ...v };
          for (const f of fields) {
            if (f.kind === "list" && raw[f.key] !== undefined) merged[f.key] = textToList(raw[f.key]!, f.key === "hard_skills" || f.key === "soft_skills");
          }
          onSave(section === "preferences_logistics" ? logisticsPayload(merged) : merged);
        }}
      >
        {section === "preferences_logistics" && <LogisticsFields values={v} onChange={setV} canEdit={canEdit} />}
        {fields.map((f) => (
          <Field key={f.key} label={f.label} required={typeof f.required === "function" ? f.required(v) : f.required} id={`${section}-${f.key}`}>
            {f.kind === "text" && <input id={`${section}-${f.key}`} value={String(v[f.key] ?? "")} disabled={!canEdit} onChange={(e) => setV({ ...v, [f.key]: e.target.value })} />}
            {f.kind === "number" && <input id={`${section}-${f.key}`} type="number" min={0} value={v[f.key] === undefined || v[f.key] === null ? "" : String(v[f.key])} disabled={!canEdit} onChange={(e) => setV({ ...v, [f.key]: e.target.value === "" ? null : Number(e.target.value) })} />}
            {f.kind === "textarea" && <textarea id={`${section}-${f.key}`} rows={3} value={String(v[f.key] ?? "")} disabled={!canEdit} onChange={(e) => setV({ ...v, [f.key]: e.target.value })} />}
            {f.kind === "select" && (
              <select id={`${section}-${f.key}`} value={String(v[f.key] ?? "")} disabled={!canEdit} onChange={(e) => setV({ ...v, [f.key]: e.target.value || null })}>
                <option value="">—</option>
                {f.options?.map((o) => (
                  <option key={o} value={o}>
                    {o.replace(/_/g, " ")}
                  </option>
                ))}
              </select>
            )}
            {f.kind === "bool" && (
              <select id={`${section}-${f.key}`} value={v[f.key] === true ? "yes" : v[f.key] === false ? "no" : ""} disabled={!canEdit} onChange={(e) => setV({ ...v, [f.key]: e.target.value === "" ? null : e.target.value === "yes" })}>
                <option value="">—</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            )}
            {f.kind === "list" && <textarea id={`${section}-${f.key}`} rows={3} value={raw[f.key] ?? listToText(v[f.key])} disabled={!canEdit} onChange={(e) => setRaw({ ...raw, [f.key]: e.target.value })} />}
          </Field>
        ))}
        {canEdit && (
          <div className="f full row" style={{ gap: 8 }}>
            <button type="submit" className="btn" disabled={busy}>
              {busy ? "Saving…" : "Save section"}
            </button>
          </div>
        )}
      </form>
    </details>
  );
}

// ---- the call ------------------------------------------------------------------------------------------------------------------

function Call({ it }: { it: Intake }) {
  const rid = it.requisition_id;
  const { can } = useSession();
  const held = useWrite((i: Parameters<typeof intakeRecordHeld>[0], k) => intakeRecordHeld(i, k), [["intake", rid], ["today"]]);
  const [dlg, setDlg] = useState(false);
  const people = [
    { id: String(it.part0.hiring_manager), label: "Hiring Manager" },
    { id: String(it.part0.hrbp), label: "HR Business Partner" },
    { id: String(it.part0.recruiter), label: "Recruiter" },
  ];
  const [standIn, setStandIn] = useState(false);
  // the call's parties for the record-held dialog (CR-018): the Hiring Manager and the Recruiter required, the HR
  // Business Partner optional; a seat with nobody in it is left out (the server refuses an unassigned requisition)
  const heldParties: HeldAttendee[] = (
    [
      ["hm", personId(it, "Hiring Manager"), String(it.part0.hiring_manager ?? "")],
      ["recruiter", personId(it, "Recruiter"), String(it.part0.recruiter ?? "")],
      ["hrbp", personId(it, "HR Business Partner"), String(it.part0.hrbp ?? "")],
    ] as const
  )
    .filter(([, pid]) => pid && pid !== "undefined" && pid !== "null")
    .map(([seat, pid, name]) => ({ seat, person_id: pid, name: name && name !== "undefined" && name !== "null" ? name : pid }));
  // §4.20: a seat holder who cannot be at the call names somebody to attend for them, from the attendee list itself
  const standIns = useRead(["delegations", rid], () => delegationList({ requisition_id: rid }), { retry: false, enabled: can("delegation.list") });
  const bookStandIn = useWrite((i: Parameters<typeof delegationGrant>[0], k) => delegationGrant(i, k), [["delegations", rid], ["delegations"], ["intake", rid], ["today"]]);
  // POLICY §5 refuses the requisition's own seat holders as stand-ins, so the picker never offers them: for each seat it
  // offers only the people the server already names as a stand-in on THAT seat here — everybody else is typed in.
  const seatIds = new Set(people.map((p) => personId(it, p.label)));
  const known = (seat: string) => new Map<string, string>((standIns.data?.delegations ?? []).filter((d) => d.seat === seat && !seatIds.has(d.grantee_id)).map((d) => [d.grantee_id, d.grantee_name]));
  const live = (standIns.data?.delegations ?? []).filter((d) => d.live && d.status === "active");
  const m = it.meeting;
  // The call is scheduled on B5's card (scheduling/ScheduleSection, S12): "Coordinator schedules required HM + Recruiter
  // intake; HRBP optional." The attendees, stand-ins and Record as held stay here (V1 §4.20, B4).
  return (
    <>
    <McPage>
      {/* B13: the HM's windows first and their length (DC-231), "Call held offline — continue" (DC-237, C5) for every
          seat intake.open names (can.record_held_offline), and the held state the call's card then reads */}
      <ScheduleSection
        subject={{ kind: "intake", rid }}
        availabilityLine={it.part1_text?.availability_line ?? null}
        heldOffline={{ can: !!it.can.record_held_offline, attendees: heldParties, onRecorded: () => go(`requisitions/${rid}/intake/part2`) }}
        held={m?.held_at ? { at: String(m.held_at), offline: m.held_offline === true, reason: typeof m.offline_reason === "string" ? m.offline_reason : null } : null}
      />
    </McPage>
    {/* DC-231, DC-239 (walks v2-b13-intake-step2, v2-b13-coordinator-intake): the call page carries Part 1's two lines —
        the HM's windows and the logistics choices — for every reader, the Coordinator included, booked or not */}
    {it.part1_text?.availability_line || it.part1_text?.logistics_line ? <Part1Lines it={it} /> : null}
    <div className="grid grid--2">
      <section className="card">
        <div className="hrow">
          <h2 className="sec">Attendees</h2>
          {can("delegation.grant") && (
            <button className="btn btn--sm" onClick={() => setStandIn(true)}>
              Book a stand-in
            </button>
          )}
        </div>
        <ul className="plain attendees">
          {people.map((p) => (
            <li key={p.label} className="attendee">
              <b>{p.id}</b>
              <span className="muted">{p.label === "HR Business Partner" ? "HR Business Partner · optional" : p.label}</span>
              {live
                .filter((d) => d.grantor_id === personId(it, p.label))
                .map((d) => (
                  // UAT F-46: only the non-empty parts are joined, so a stand-in with no ticked scope has no empty part
                  <span key={d.id} className="attendee__standin">
                    {[`stand-in: ${d.grantee_name}`, ...d.capability_labels, `until ${fmtDate(d.valid_to)}`].filter(Boolean).join(" · ")}
                  </span>
                ))}
            </li>
          ))}
        </ul>
        {bookStandIn.isSuccess && <Receipt>Booked — they may act for that seat, under their own name, until the last day.</Receipt>}
        <StandInDialog
          title="Book a stand-in"
          confirm="Book the stand-in"
          open={standIn}
          onClose={() => setStandIn(false)}
          busy={bookStandIn.isPending}
          error={bookStandIn.error}
          known={known}
          directory={it.delegation_directory as Parameters<typeof StandInDialog>[0]["directory"]}
          onSubmit={(v) => void bookStandIn.mutateAsync({ requisition_id: rid, seat: v.seat, grantee_id: v.grantee, capabilities: v.capabilities, valid_from: new Date(`${v.from}T00:00:00`).toISOString(), valid_to: new Date(`${v.to}T17:00:00`).toISOString(), ...(v.reason ? { reason: v.reason } : {}) }).then(() => setStandIn(false))}
        />
        <h3 className="sub">Record the call as held</h3>
        {m?.held_at && m.held_offline === true ? (
          <Receipt>{heldOfflinePart2Open({ date: fmtDate(String(m.held_at), true) })}</Receipt>
        ) : m?.held_at ? (
          <Receipt>Held {fmtDate(String(m.held_at), true)} — Part 2 is open.</Receipt>
        ) : it.can.record_held ? (
          <>
            <p className="muted">Attendance — the Hiring Manager and the Recruiter are required, the HR Business Partner is optional; an absent required party needs an explicit exception with a reason that prints on the requisition.</p>
            <button className="btn btn--primary" onClick={() => setDlg(true)}>
              Record as held
            </button>
          </>
        ) : (
          <Empty heading="Call not recorded yet">Once the call happens, the Recruiter records it here; that opens Part 2.</Empty>
        )}
        {dlg ? (
          <McRoot>
            <RecordHeld
              open
              attendees={heldParties}
              record={(input) => held.mutateAsync({ requisition_id: rid, ...input })}
              onClose={() => setDlg(false)}
              onRecorded={() => {
                setDlg(false);
                go(`requisitions/${rid}/intake/part2`);
              }}
            />
          </McRoot>
        ) : null}
      </section>
    </div>
    </>
  );
}

function personId(it: Intake, label: string): string {
  // the intake projection carries names for display; the attendance list keys on the requisition's seats
  const seats = it.part0 as Record<string, unknown>;
  return label === "Hiring Manager" ? String(seats.hiring_manager_id ?? seats.hiring_manager) : label === "HR Business Partner" ? String(seats.hrbp_id ?? seats.hrbp) : String(seats.recruiter_id ?? seats.recruiter);
}

export function useIntakeMemo(it: Intake) {
  return useMemo(() => it, [it]);
}
